import React from 'react';
import { 
  Dog, 
  Stethoscope, 
  Wrench, 
  Building2, 
  Boxes, 
  ClipboardList, 
  Users, 
  Calendar, 
  FileText, 
  Receipt, 
  Building, 
  Wallet, 
  Syringe, 
  FileArchive, 
  PenTool, 
  ShieldCheck, 
  Settings, 
  LayoutDashboard, 
  BarChart3, 
  TrendingUp, 
  Scissors,
  CreditCard,
  Scale,
  Utensils,
  Shirt,
  DollarSign
} from 'lucide-react';
import { AppView } from '../../../../types';

export type EnterpriseVerticalId = 'pet_vet' | 'medical' | 'technical_service' | 'general_corporate' | 'food_icecream' | 'fashion_retail' | 'retail_bulk';

export interface VerticalPreset {
  id: EnterpriseVerticalId;
  name: string;
  badgeLabel: string;
  badgeIcon: React.ComponentType<{ className?: string }>;
  description: string;
  terminology: {
    clientLabel: string;
    clientPluralLabel: string;
    itemLabel: string;
    itemPluralLabel: string;
    agendaLabel: string;
    quickActionLabel: string;
  };
  agendaPresetServices: {
    name: string;
    category: 'banho_tosa' | 'clinica' | 'seguro_vistoria' | 'servico_tecnico' | 'geral';
    durationMinutes: number;
    priceSuggestion: number;
    color: string;
  }[];
  departments: {
    id: string;
    name: string;
    items: {
      id: AppView;
      label: string;
      icon: React.ComponentType<{ className?: string }>;
      badgeKey?: 'quotes' | 'orders' | 'expiring' | 'vaccines';
      badgeVariant?: 'blue' | 'amber' | 'emerald' | 'purple';
    }[];
  }[];
}

export const ENTERPRISE_VERTICALS: Record<EnterpriseVerticalId, VerticalPreset> = {
  pet_vet: {
    id: 'pet_vet',
    name: 'Veterinária & Pet Shop',
    badgeLabel: 'Veterinária & Pet Shop',
    badgeIcon: Dog,
    description: 'Agenda da clínica, consultas veterinárias, banho e tosa, vacinas e ficha dos pets.',
    terminology: {
      clientLabel: 'Tutor (Dono do Pet)',
      clientPluralLabel: 'Tutores & Clientes',
      itemLabel: 'Pet',
      itemPluralLabel: 'Pets Cadastrados',
      agendaLabel: 'Agenda da Clínica & Pet Shop',
      quickActionLabel: 'Agendar Horário (Consulta ou Banho)'
    },
    agendaPresetServices: [
      { name: 'Consulta com Médico Veterinário', category: 'clinica', durationMinutes: 40, priceSuggestion: 150, color: 'bg-blue-100 text-blue-900 border-blue-200' },
      { name: 'Aplicação de Vacina / Imunização', category: 'clinica', durationMinutes: 20, priceSuggestion: 90, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
      { name: 'Retorno Veterinário', category: 'clinica', durationMinutes: 30, priceSuggestion: 0, color: 'bg-teal-100 text-teal-900 border-teal-200' },
      { name: 'Banho Simples (Porte Pequeno/Médio)', category: 'banho_tosa', durationMinutes: 45, priceSuggestion: 55, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
      { name: 'Banho Simples (Porte Grande)', category: 'banho_tosa', durationMinutes: 60, priceSuggestion: 80, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
      { name: 'Tosa Higiênica + Banho', category: 'banho_tosa', durationMinutes: 60, priceSuggestion: 90, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Banho & Tosa Geral na Máquina', category: 'banho_tosa', durationMinutes: 75, priceSuggestion: 110, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Tosa na Tesoura Especializada', category: 'banho_tosa', durationMinutes: 90, priceSuggestion: 140, color: 'bg-amber-100 text-amber-900 border-amber-200' },
      { name: 'Hospedagem / Hotelzinho (Diária)', category: 'geral', durationMinutes: 480, priceSuggestion: 80, color: 'bg-rose-100 text-rose-900 border-rose-200' }
    ],
    departments: [
      {
        id: 'gestao',
        name: 'Visão Geral da Empresa',
        items: [
          { id: 'erp-hub', label: 'Painel Geral', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & Lucro', icon: BarChart3 },
          { id: 'market-radar', label: 'Pesquisa de Preços da Região', icon: TrendingUp }
        ]
      },
      {
        id: 'atendimento_pet',
        name: 'Atendimento & Recepção',
        items: [
          { id: 'recepcao_pdv', label: 'Balcão da Recepção & Checkout', icon: CreditCard },
          { id: 'calendar', label: 'Agenda da Clínica & Pet', icon: Calendar },
          { id: 'clients', label: 'Tutores & Clientes', icon: Users },
          { id: 'receipts', label: 'Recibos & Pagamentos', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign },
          { id: 'work-orders', label: 'Fichas de Serviço & Daycare', icon: ClipboardList, badgeKey: 'orders', badgeVariant: 'amber' }
        ]
      },
      {
        id: 'clinica_vet',
        name: 'Consultas & Procedimentos',
        items: [
          { id: 'prontuario_soap', label: 'Ficha Clínica do Pet', icon: Stethoscope },
          { id: 'retorno_vacinas', label: 'Lembretes de Vacinas por WhatsApp', icon: Syringe, badgeKey: 'vaccines', badgeVariant: 'purple' },
          { id: 'cofre_exames', label: 'Pasta de Exames e Fotos', icon: FileArchive }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Estoque & Farmácia',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Vacinas & Lotes', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores de Produtos & Ração', icon: Building },
          { id: 'costs', label: 'Despesas Fixas da Empresa', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade & Impostos',
        items: [
          { id: 'portal_contador', label: 'Envio de Documentos para o Contador', icon: Building2 },
          { id: 'signatures', label: 'Termos de Autorização Assinados', icon: PenTool }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Veterinários e Tosadores', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Empresa', icon: Settings }
        ]
      }
    ]
  },

  medical: {
    id: 'medical',
    name: 'Consultório Médico & Saúde',
    badgeLabel: 'Consultório Médico & Saúde',
    badgeIcon: Stethoscope,
    description: 'Ficha clínica do paciente, histórico de consultas, exames e receitas.',
    terminology: {
      clientLabel: 'Paciente',
      clientPluralLabel: 'Pacientes Cadastrados',
      itemLabel: 'Ficha do Paciente',
      itemPluralLabel: 'Fichas Clínicas',
      agendaLabel: 'Agenda de Consultas',
      quickActionLabel: 'Nova Consulta'
    },
    agendaPresetServices: [
      { name: 'Primeira Consulta Médica', category: 'clinica', durationMinutes: 60, priceSuggestion: 300, color: 'bg-blue-100 text-blue-900 border-blue-200' },
      { name: 'Consulta de Retorno', category: 'clinica', durationMinutes: 30, priceSuggestion: 0, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
      { name: 'Procedimento / Pequena Cirurgia', category: 'clinica', durationMinutes: 60, priceSuggestion: 450, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
      { name: 'Exame Clínico / Avaliação', category: 'clinica', durationMinutes: 45, priceSuggestion: 200, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Consulta Online / Telemedicina', category: 'clinica', durationMinutes: 30, priceSuggestion: 220, color: 'bg-amber-100 text-amber-900 border-amber-200' }
    ],
    departments: [
      {
        id: 'gestao',
        name: 'Visão Geral',
        items: [
          { id: 'erp-hub', label: 'Painel do Consultório', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & Lucro', icon: BarChart3 },
          { id: 'market-radar', label: 'Valores Médios da sua Cidade', icon: TrendingUp }
        ]
      },
      {
        id: 'atendimento_medico',
        name: 'Recepção & Pacientes',
        items: [
          { id: 'recepcao_pdv', label: 'Caixa & Recepção de Atendimentos', icon: CreditCard },
          { id: 'calendar', label: 'Agenda de Consultas', icon: Calendar },
          { id: 'clients', label: 'Cadastro de Pacientes', icon: Users },
          { id: 'receipts', label: 'Recibos para Imposto de Renda', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign },
          { id: 'history', label: 'Orçamentos de Tratamentos', icon: FileText, badgeKey: 'quotes', badgeVariant: 'blue' }
        ]
      },
      {
        id: 'clinica_humana',
        name: 'Atendimento Médico',
        items: [
          { id: 'prontuario_soap', label: 'Prontuário e Histórico do Paciente', icon: Stethoscope },
          { id: 'cofre_exames', label: 'Exames e Laudos Anexados', icon: FileArchive },
          { id: 'signatures', label: 'Termos de Consentimento Assinados', icon: PenTool }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Medicamentos & Materiais',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Medicamentos & Lotes', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores Hospitalares', icon: Building },
          { id: 'costs', label: 'Despesas Fixas do Consultório', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Envio de Recibos para o Contador', icon: Building2 }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Médicos e Atendentes', icon: ShieldCheck },
          { id: 'profile', label: 'Dados do Consultório', icon: Settings }
        ]
      }
    ]
  },

  technical_service: {
    id: 'technical_service',
    name: 'Oficina, Mecânica & Seguradoras',
    badgeLabel: 'Oficina & Seguradoras',
    badgeIcon: Wrench,
    description: 'Ordens de serviço, vistorias de seguros, franquias, peças e garantias.',
    terminology: {
      clientLabel: 'Cliente / Segurado',
      clientPluralLabel: 'Clientes & Segurados',
      itemLabel: 'Veículo / Aparelho',
      itemPluralLabel: 'Veículos & Equipamentos',
      agendaLabel: 'Agendamento de Serviços & Vistorias',
      quickActionLabel: 'Nova Ordem de Serviço'
    },
    agendaPresetServices: [
      { name: 'Recepção / Triagem Inicial do Veículo ou Item', category: 'servico_tecnico', durationMinutes: 30, priceSuggestion: 0, color: 'bg-blue-100 text-blue-900 border-blue-200' },
      { name: 'Vistoria de Sinistro / Seguradora', category: 'seguro_vistoria', durationMinutes: 45, priceSuggestion: 0, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Diagnóstico & Orçamento do Conserto', category: 'servico_tecnico', durationMinutes: 60, priceSuggestion: 120, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
      { name: 'Execução Mecânica / Funilaria', category: 'servico_tecnico', durationMinutes: 180, priceSuggestion: 350, color: 'bg-amber-100 text-amber-900 border-amber-200' },
      { name: 'Entrega Técnica com Termo de Garantia', category: 'servico_tecnico', durationMinutes: 30, priceSuggestion: 0, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' }
    ],
    departments: [
      {
        id: 'gestao',
        name: 'Visão Geral da Oficina',
        items: [
          { id: 'erp-hub', label: 'Painel da Oficina', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & Lucro', icon: BarChart3 },
          { id: 'market-radar', label: 'Preço Médio da Mão de Obra', icon: TrendingUp }
        ]
      },
      {
        id: 'operacao_os',
        name: 'Serviços & Seguradoras',
        items: [
          { id: 'work-orders', label: 'Ordens de Serviço & Consertos', icon: ClipboardList, badgeKey: 'orders', badgeVariant: 'amber' },
          { id: 'calendar', label: 'Agendamento de Serviços & Vistorias', icon: Calendar },
          { id: 'history', label: 'Orçamentos & Seguradoras', icon: FileText, badgeKey: 'quotes', badgeVariant: 'blue' },
          { id: 'clients', label: 'Clientes & Segurados', icon: Users },
          { id: 'receipts', label: 'Recibos e Comprovantes', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign },
          { id: 'signatures', label: 'Termo de Garantia Assinado pelo Cliente', icon: PenTool }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Peças & Materiais',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Peças & Lotes', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores de Peças', icon: Building },
          { id: 'costs', label: 'Despesas Fixas da Oficina', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Envio de Documentos para o Contador', icon: Building2 }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Mecânicos e Funcionários', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Oficina', icon: Settings }
        ]
      }
    ]
  },

  general_corporate: {
    id: 'general_corporate',
    name: 'Gestão Empresarial',
    badgeLabel: 'Gestão Empresarial',
    badgeIcon: Building2,
    description: 'Orçamentos, ordens de serviço, controle de estoque e financeiro simples.',
    terminology: {
      clientLabel: 'Cliente',
      clientPluralLabel: 'Clientes Cadastrados',
      itemLabel: 'Serviço / Projeto',
      itemPluralLabel: 'Serviços & Contratos',
      agendaLabel: 'Agenda de Atendimentos',
      quickActionLabel: 'Novo Orçamento'
    },
    agendaPresetServices: [
      { name: 'Reunião com Cliente', category: 'geral', durationMinutes: 60, priceSuggestion: 0, color: 'bg-blue-100 text-blue-900 border-blue-200' },
      { name: 'Visita Técnica / Medição', category: 'servico_tecnico', durationMinutes: 90, priceSuggestion: 150, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' },
      { name: 'Apresentação de Proposta', category: 'geral', durationMinutes: 45, priceSuggestion: 0, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
      { name: 'Execução de Serviço em Campo', category: 'servico_tecnico', durationMinutes: 180, priceSuggestion: 400, color: 'bg-amber-100 text-amber-900 border-amber-200' }
    ],
    departments: [
      {
        id: 'gestao',
        name: 'Visão Geral da Empresa',
        items: [
          { id: 'erp-hub', label: 'Painel Geral', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & Lucro', icon: BarChart3 },
          { id: 'market-radar', label: 'Pesquisa de Preços da sua Cidade', icon: TrendingUp }
        ]
      },
      {
        id: 'comercial',
        name: 'Vendas & Atendimento',
        items: [
          { id: 'recepcao_pdv', label: 'Frente de Caixa & Checkout', icon: CreditCard },
          { id: 'history', label: 'Orçamentos & Propostas', icon: FileText, badgeKey: 'quotes', badgeVariant: 'blue' },
          { id: 'work-orders', label: 'Ordens de Serviço', icon: ClipboardList, badgeKey: 'orders', badgeVariant: 'amber' },
          { id: 'clients', label: 'Cadastro de Clientes', icon: Users },
          { id: 'receipts', label: 'Emissão de Recibos', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign },
          { id: 'calendar', label: 'Agenda de Atendimentos', icon: Calendar }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Controle de Estoque',
        items: [
          { id: 'estoque_hub', label: 'Estoque & Lotes', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores', icon: Building },
          { id: 'costs', label: 'Despesas Fixas da Empresa', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Envio de Relatórios para o Contador', icon: Building2 },
          { id: 'signatures', label: 'Documentos e Contratos Assinados', icon: PenTool }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Funcionários & Colaboradores', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Empresa', icon: Settings }
        ]
      }
    ]
  },

  food_icecream: {
    id: 'food_icecream',
    name: 'Sorveteria, Açaí & Buffet por Quilo',
    badgeLabel: 'Sorveteria & Açaí',
    badgeIcon: Utensils,
    description: 'Frente de caixa Speed-Checkout com balança serial, gestão de taras, controle de buffet e custos de refrigeração.',
    terminology: {
      clientLabel: 'Cliente (Balcão)',
      clientPluralLabel: 'Clientes Atendidos',
      itemLabel: 'Comanda / Pesagem',
      itemPluralLabel: 'Comandas e Vendas',
      agendaLabel: 'Escala de Operadores',
      quickActionLabel: 'Nova Pesagem (Speed-Checkout)'
    },
    agendaPresetServices: [
      { name: 'Sorvete / Açaí no Quilo (Buffet)', category: 'geral', durationMinutes: 5, priceSuggestion: 79.90, color: 'bg-amber-100 text-amber-900 border-amber-200' },
      { name: 'Taça Especial / Sundae Montado', category: 'geral', durationMinutes: 10, priceSuggestion: 28.00, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Pote Térmico Viagem 1 Litro', category: 'geral', durationMinutes: 5, priceSuggestion: 45.00, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' }
    ],
    departments: [
      {
        id: 'pdv_frente',
        name: 'Frente de Caixa & Pesagem',
        items: [
          { id: 'speed_pos', label: 'Speed-Checkout (Balança Serial & Balcão)', icon: Scale },
          { id: 'receipts', label: 'Cupons & Recibos Emitidos', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign }
        ]
      },
      {
        id: 'gestao',
        name: 'Visão Geral & Lucro',
        items: [
          { id: 'erp-hub', label: 'Painel Geral', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & DRE', icon: BarChart3 },
          { id: 'market-radar', label: 'Preços Médios de Sorvete/Kg', icon: TrendingUp }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Estoque & Custos de Refrigeração',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Insumos & Embalagens', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores de Frutas e Bases', icon: Building },
          { id: 'costs', label: 'Energia Elétrica & Freezers 24/7', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Fechamento Mensal para Contador', icon: Building2 }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Operadores de Caixa e Atendentes', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Sorveteria', icon: Settings }
        ]
      }
    ]
  },

  fashion_retail: {
    id: 'fashion_retail',
    name: 'Loja de Roupas, Boutique & Varejo de Moda',
    badgeLabel: 'Moda & Vestuário',
    badgeIcon: Shirt,
    description: 'Frente de caixa com leitor de código de barras, grade de tamanhos e cores, mala condicional (48h) e markup de atacado.',
    terminology: {
      clientLabel: 'Cliente',
      clientPluralLabel: 'Clientes Cadastrados',
      itemLabel: 'Peça de Vestuário',
      itemPluralLabel: 'Peças em Estoque',
      agendaLabel: 'Condicionais & Provas Agendadas',
      quickActionLabel: 'Nova Venda de Balcão'
    },
    agendaPresetServices: [
      { name: 'Entrega de Mala Condicional (48h)', category: 'geral', durationMinutes: 15, priceSuggestion: 0, color: 'bg-pink-100 text-pink-900 border-pink-200' },
      { name: 'Devolução / Acerto de Condicional', category: 'geral', durationMinutes: 20, priceSuggestion: 0, color: 'bg-purple-100 text-purple-900 border-purple-200' },
      { name: 'Consultoria de Estilo / Provador VIP', category: 'geral', durationMinutes: 60, priceSuggestion: 120, color: 'bg-indigo-100 text-indigo-900 border-indigo-200' }
    ],
    departments: [
      {
        id: 'pdv_moda',
        name: 'Vendas & Frente de Caixa',
        items: [
          { id: 'recepcao_pdv', label: 'Balcão de Vendas & Bipador USB', icon: CreditCard },
          { id: 'receipts', label: 'Comprovantes & Trocas', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign },
          { id: 'clients', label: 'Clientes & Tamanhos Preferidos', icon: Users },
          { id: 'calendar', label: 'Controle de Malas Condicionais', icon: Calendar }
        ]
      },
      {
        id: 'gestao',
        name: 'Visão Geral & Lucro',
        items: [
          { id: 'erp-hub', label: 'Painel Geral', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'DRE & Margem por Coleção', icon: BarChart3 },
          { id: 'market-radar', label: 'Pesquisa de Preços da sua Cidade', icon: TrendingUp }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Estoque & Grade de Tamanhos',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Peças (P/M/G/Cores)', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores de Confecção & Atacado', icon: Building },
          { id: 'costs', label: 'Despesas Fixas da Loja', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Envio de Vendas para o Contador', icon: Building2 }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Vendedoras & Comissões', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Loja', icon: Settings }
        ]
      }
    ]
  },

  retail_bulk: {
    id: 'retail_bulk',
    name: 'Varejo, Empório & Produtos a Granel',
    badgeLabel: 'Empório & Granel',
    badgeIcon: Scale,
    description: 'Frente de caixa Speed-Checkout com balança serial contínua, leitura de etiquetas de gôndola, controle de lotes/validade e exportação Toledo MGV.',
    terminology: {
      clientLabel: 'Cliente (Balcão)',
      clientPluralLabel: 'Clientes Atendidos',
      itemLabel: 'Item / Pesagem',
      itemPluralLabel: 'Itens Pesados & Produtos',
      agendaLabel: 'Escala de Operadores',
      quickActionLabel: 'Nova Pesagem (Speed-Checkout)'
    },
    agendaPresetServices: [
      { name: 'Fracionamento & Pesagem a Granel', category: 'geral', durationMinutes: 5, priceSuggestion: 0, color: 'bg-amber-100 text-amber-900 border-amber-200' },
      { name: 'Corte Especial / Fatiamento de Frios', category: 'geral', durationMinutes: 10, priceSuggestion: 0, color: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
      { name: 'Montagem de Cesta Personalizada', category: 'geral', durationMinutes: 20, priceSuggestion: 25, color: 'bg-purple-100 text-purple-900 border-purple-200' }
    ],
    departments: [
      {
        id: 'pdv_frente',
        name: 'Frente de Caixa & Pesagem',
        items: [
          { id: 'speed_pos', label: 'Speed-Checkout (Balança Serial & Balcão)', icon: Scale },
          { id: 'receipts', label: 'Cupons & Recibos Emitidos', icon: Receipt },
          { id: 'financial_hub', label: 'Contas a Pagar & Receber', icon: DollarSign }
        ]
      },
      {
        id: 'gestao',
        name: 'Visão Geral & Lucro',
        items: [
          { id: 'erp-hub', label: 'Painel Geral', icon: LayoutDashboard },
          { id: 'executive-cockpit', label: 'Resumo Financeiro & DRE', icon: BarChart3 },
          { id: 'market-radar', label: 'Pesquisa de Preços da Região', icon: TrendingUp }
        ]
      },
      {
        id: 'suprimentos',
        name: 'Estoque, Lotes & Validades',
        items: [
          { id: 'estoque_hub', label: 'Estoque de Insumos, Sacarias & Lotes', icon: Boxes, badgeKey: 'expiring', badgeVariant: 'amber' },
          { id: 'suppliers', label: 'Fornecedores & Entrada por NF-e', icon: Building },
          { id: 'costs', label: 'Custos Operacionais & Despesas', icon: Wallet }
        ]
      },
      {
        id: 'fiscal',
        name: 'Contabilidade',
        items: [
          { id: 'portal_contador', label: 'Fechamento Mensal para Contador', icon: Building2 }
        ]
      },
      {
        id: 'governanca',
        name: 'Nossa Equipe',
        items: [
          { id: 'equipe' as any, label: 'Operadores de Caixa e Atendentes', icon: ShieldCheck },
          { id: 'profile', label: 'Dados da Loja', icon: Settings }
        ]
      }
    ]
  }
};

/**
 * Detecta a vertical empresarial mais adequada com base no nicho e profissão cadastrados.
 */
export function detectVerticalFromNiche(niche?: string, profession?: string): EnterpriseVerticalId {
  const text = `${niche || ''} ${profession || ''}`.toLowerCase();

  // 0. Sorveteria, Açaí e Alimentação por Quilo — verificado ANTES de retail_bulk para evitar falso match
  if (
    text.includes('sorvet') || 
    text.includes('acai') || 
    text.includes('açaí') || 
    text.includes('gelat') || 
    text.includes('picole') || 
    text.includes('picolé') || 
    text.includes('paleta') || 
    text.includes('buffet') || 
    text.includes('self-service') || 
    text.includes('self service')
  ) {
    return 'food_icecream';
  }

  // 0. Loja de Roupas, Boutique e Moda
  if (
    text.includes('roupa') || 
    text.includes('boutique') || 
    text.includes('vestuario') || 
    text.includes('vestuário') || 
    text.includes('moda') || 
    text.includes('calcado') || 
    text.includes('calçado') || 
    text.includes('calcados') || 
    text.includes('calçados') || 
    text.includes('confeccao') || 
    text.includes('confecção') || 
    text.includes('brecho') || 
    text.includes('brechó')
  ) {
    return 'fashion_retail';
  }

  // 0. Varejo, Empório, Agropecuária, Produtos a Granel, Açougue, Hortifrúti, Padaria
  if (
    text.includes('emporio') || 
    text.includes('empório') || 
    text.includes('granel') || 
    text.includes('agropec') || 
    text.includes('racao') || 
    text.includes('ração') || 
    text.includes('acougue') || 
    text.includes('açougue') || 
    text.includes('hortifruti') || 
    text.includes('hortifrúti') || 
    text.includes('sacolao') || 
    text.includes('sacolão') || 
    text.includes('quitanda') || 
    text.includes('padaria') || 
    text.includes('panific') || 
    text.includes('queij') || 
    text.includes('frios') || 
    text.includes('mercearia') || 
    text.includes('produtos naturais')
  ) {
    return 'retail_bulk';
  }

  // 1. Veterinária e Pet Shop
  if (
    text.includes('pet') || 
    text.includes('veterin') || 
    text.includes('animal') || 
    text.includes('canil') || 
    text.includes('tosa') || 
    text.includes('banho') || 
    text.includes('dog') || 
    text.includes('gato') ||
    text.includes('felino')
  ) {
    return 'pet_vet';
  }

  // 2. Médico e Saúde Humana
  if (
    text.includes('medico') || 
    text.includes('médic') || 
    text.includes('saude') || 
    text.includes('saúde') || 
    text.includes('doutor') || 
    text.includes('clinica') || 
    text.includes('clínica') || 
    text.includes('odont') || 
    text.includes('dentista') || 
    text.includes('psicolog') || 
    text.includes('psicólog') || 
    text.includes('fisiot') || 
    text.includes('terapia') || 
    text.includes('hospital') ||
    text.includes('dermato')
  ) {
    return 'medical';
  }

  // 3. Técnico, Oficinas e Seguros
  if (
    text.includes('oficina') || 
    text.includes('mecanic') || 
    text.includes('mecânic') || 
    text.includes('automovel') || 
    text.includes('automóvel') || 
    text.includes('autopeca') || 
    text.includes('autopeça') || 
    text.includes('auto ') || 
    text.includes('seguro auto') || 
    text.includes('seguro veic') || 
    text.includes('carro') || 
    text.includes('veicul') || 
    text.includes('veícul') || 
    text.includes('funilari') || 
    text.includes('lataria') || 
    text.includes('pintura auto') || 
    text.includes('motocicleta') || 
    text.includes('mototaxi') || 
    text.includes('mototáxi') || 
    text.includes('moto ') || 
    text.includes('ar condicionado') || 
    text.includes('hvac') || 
    text.includes('climatiz') || 
    text.includes('eletric') || 
    text.includes('eletrôn') || 
    text.includes('assistencia') || 
    text.includes('assistência') ||
    text.includes('repar')
  ) {
    return 'technical_service';
  }

  // 4. Padrão: Gestão Empresarial
  return 'general_corporate';
}
